import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataTest, DataTestType, SchemaTestForm } from '@y42/data-test';
import { isNullish } from '@y42/util/nullish';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTestColumnsCellRendererUIModel } from './data-test-columns-cell-renderer.model';

@UntilDestroy()
@Injectable()
export class DataTestColumnsExtractorService {
  private readonly columnsExtractorsDef: Record<
    DataTestType,
    (dataTest: DataTest, schemaDataTest: SchemaTestForm | null) => DataTestColumnsCellRendererUIModel[]
  > = {
    [DataTestType.NumRowDrift]: () => this.rowDriftColumnsExtractor(),
    [DataTestType.LowCode]: (dataTest, schemaTest) => this.lowCodeColumnsExtractor(dataTest, schemaTest),
    [DataTestType.Unique]: (dataTest, schemaTest) => this.uniqueColumnsExtractor(dataTest, schemaTest),
    [DataTestType.Column]: (dataTest, schemaTest) => this.columnColumnsExtractor(dataTest, schemaTest),
    [DataTestType.Schema]: (dataTest) => this.schemaColumnsExtractor(dataTest),
    [DataTestType.ReferentialIntegrity]: (dataTest, schemaTest) =>
      this.referentialIntegrityColumnsExtractor(dataTest, schemaTest),
  };

  getColumns(
    dataTest$: Observable<DataTest | null | undefined>,
    schemaDataTest$: Observable<SchemaTestForm | null | undefined>,
  ): Observable<DataTestColumnsCellRendererUIModel[]> {
    return combineLatest([dataTest$, schemaDataTest$]).pipe(
      map(([dataTest, schemaTest]): DataTestColumnsCellRendererUIModel[] => {
        if (isNullish(dataTest) || isNullish(schemaTest)) {
          return [];
        }

        return this.columnsExtractorsDef[dataTest.type](dataTest, schemaTest);
      }),
      // sort by name
      map((columns) => [...columns].sort((a, b) => a.columnName.localeCompare(b.columnName))),
      untilDestroyed(this),
    );
  }

  private schemaColumnsExtractor(dataTest: DataTest): DataTestColumnsCellRendererUIModel[] {
    if (dataTest.type !== DataTestType.Schema) {
      return [];
    }

    return this.getColumnsFromSchemaByColumnName(
      dataTest.schema.map((x) => x.column),
      dataTest,
    );
  }

  private rowDriftColumnsExtractor(): DataTestColumnsCellRendererUIModel[] {
    return [
      {
        columnName: 'All',
      },
    ];
  }

  private columnColumnsExtractor(
    dataTest: DataTest,
    schemaTest: SchemaTestForm | null,
  ): DataTestColumnsCellRendererUIModel[] {
    if (isNullish(schemaTest) || dataTest.type !== DataTestType.Column) {
      return [];
    }

    return this.getColumnsFromSchemaByColumnName([dataTest.column], schemaTest);
  }

  private uniqueColumnsExtractor(
    dataTest: DataTest,
    schemaTest: SchemaTestForm | null,
  ): DataTestColumnsCellRendererUIModel[] {
    if (isNullish(schemaTest) || dataTest.type !== DataTestType.Unique) {
      return [];
    }

    return this.getColumnsFromSchemaByColumnName([dataTest.column], schemaTest);
  }

  private lowCodeColumnsExtractor(
    dataTest: DataTest,
    schemaTest: SchemaTestForm | null,
  ): DataTestColumnsCellRendererUIModel[] {
    if (isNullish(schemaTest) || dataTest.type !== DataTestType.LowCode) {
      return [];
    }

    if (dataTest.low_code_string.length < 2) {
      return [];
    }

    const regexColumnName = /@([\w_]+)/g;
    const columns = dataTest.low_code_string.match(regexColumnName)?.map((match) => match.replace('@', '')) ?? [];

    return this.getColumnsFromSchemaByColumnName(columns, schemaTest);
  }

  private referentialIntegrityColumnsExtractor(
    dataTest: DataTest,
    schemaTest: SchemaTestForm | null,
  ): DataTestColumnsCellRendererUIModel[] {
    if (isNullish(schemaTest) || dataTest.type !== DataTestType.ReferentialIntegrity) {
      return [];
    }

    return this.getColumnsFromSchemaByColumnName([dataTest.column], schemaTest);
  }

  private getColumnsFromSchemaByColumnName(
    columnNames: string[],
    schemaTest: SchemaTestForm,
  ): DataTestColumnsCellRendererUIModel[] {
    return columnNames.map((columnName): DataTestColumnsCellRendererUIModel => {
      const columnInSchema = schemaTest.schema.find((x) => x.column === columnName) ?? null;
      if (isNullish(columnInSchema)) {
        return { columnName };
      }
      return {
        columnName: columnInSchema.column,
        columnType: columnInSchema.type,
      };
    });
  }
}
